// A custom hook that helps us determine wether testable features are on or off
// This also contains routing validation for testableRoutes
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactElement,
  useCallback,
  useMemo,
} from 'react';
import { useRouter } from 'next/router';
import { BackendTestableFeatures, AppLogger } from '@workivate/tho-web-shared';
import { ContextValue } from './testable.types';
import {
  getTestableFeaturesEnabled,
  getFeMockServerEnabled,
  setTestableFlag,
} from './testable.helpers';
import { IS_PRODUCTION } from 'utils/check-environment/check-environment';
import myFetch from 'utils/fetch';

export const TestableContext = createContext<ContextValue>({
  testableFeaturesEnabled: () => false,
  setTestableFlag: () => null,
  feMockServerEnabled: () => null,
  feMockServerUrl: () => '',
  isLoadingFeatures: true,
});

export const useTestable = () => useContext(TestableContext);

type TestableProviderProps = {
  children: React.ReactNode;
};

export const TestableProvider = ({
  children,
}: TestableProviderProps): ReactElement => {
  const [isLoadingFeatures, setIsLoadingFeatures] = useState(true); // allows components to wait until feature enablement is resolved

  const router = useRouter();

  // Initialize on mount
  useEffect(() => {
    if (!router.isReady || Object.keys(router.query).length === 0) {
      setIsLoadingFeatures(false);
      return;
    }

    const loadFeatures = async () => {
      setIsLoadingFeatures(true);

      try {
        const features =
          await BackendTestableFeatures.getTestableFeaturesEnabled({
            query: router.query,
            IS_PRODUCTION: IS_PRODUCTION,
          });

        const clientId = router.query['client_id'] as string;

        if (clientId && Object.keys(features).length > 0) {
          // On some routes we may not have a client id, and we need it to set the back end features
          // Note that we do not persist the features anywhere on front end as what we set here is not necessarily the actual values, so it would have no use
          // the originating app e.g. Hermes may have set different values and we can't know what they are
          AppLogger.log(
            'USL FE setting backend testable features overrides',
            features
          );

          await myFetch(`${process.env.IDENTITY_URL}/testable-features`, {
            method: 'POST',
            body: {
              client_id: clientId,
              testable_features: features,
            },
          });
        }
      } catch (error) {
        AppLogger.error('Failed to set backend testable features', error);
      } finally {
        setIsLoadingFeatures(false);
      }
    };
    loadFeatures();
  }, [router.isReady]);

  const testableFeaturesEnabled = useCallback((): boolean => {
    return getTestableFeaturesEnabled(router.query);
  }, [router.query]);

  const feMockServerEnabled = useCallback((): string | null => {
    return testableFeaturesEnabled() ? getFeMockServerEnabled() : null;
  }, [testableFeaturesEnabled]);

  const context: ContextValue = useMemo(
    () => ({
      testableFeaturesEnabled,
      setTestableFlag,
      feMockServerEnabled,
      feMockServerUrl: feMockServerEnabled()
        ? () => 'http://localhost:3012'
        : () => null,
      isLoadingFeatures,
    }),
    [testableFeaturesEnabled, feMockServerEnabled, isLoadingFeatures]
  );
  return (
    <TestableContext.Provider value={context}>
      {children}
    </TestableContext.Provider>
  );
};
